import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router-dom";

import OnboardingStepper from "./components/OnboardingStepper";
import CategorySelectionStep from "./steps/CategorySelectionStep";
import OrganisationProfileStep from "./steps/OrganisationProfileStep";
import WelcomeStep from "./steps/WelcomeStep";
import { isOnboardingEligibleRole } from "../../routes/onboardingAccess";
import { getRoleHomePath } from "../../routes/roleAccess";
import AuthContext from "../../store/auth-context";
import { automationTestIds } from "../../testing/automationTestIds";
import { pageRootTestIds } from "../../testing/pageRootTestIds";

const TOTAL_STEPS = 3;
const STEP_IDS = ["goal", "organisation", "get-started"];

const stepIndexFromParam = (value) => {
  const index = STEP_IDS.indexOf(value);
  return index >= 0 ? index : 0;
};

const IssuerOnboarding = () => {
  const { t } = useTranslation(["onboarding"]);
  const authContext = useContext(AuthContext);

  const userRole =
    authContext.userType?.toLowerCase() ||
    localStorage.getItem("userType")?.toLowerCase();

  const [searchParams, setSearchParams] = useSearchParams();
  const currentStep = stepIndexFromParam(searchParams.get("step"));

  const goToStep = (step) => {
    const next = Math.max(0, Math.min(step, TOTAL_STEPS - 1));
    setSearchParams(next === 0 ? {} : { step: STEP_IDS[next] });
  };

  const [isAllowed] = useState(
    () =>
      authContext.isNewUserOnboarding === true &&
      isOnboardingEligibleRole(userRole),
  );

  useEffect(() => {
    if (!isAllowed) {
      return undefined;
    }
    const warnBeforeUnload = (event) => {
      if (!localStorage.getItem("auth_token")) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [isAllowed]);

  const headingRef = useRef(null);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    headingRef.current?.focus();
  }, [currentStep]);

  const steps = useMemo(
    () => [
      { id: "goal", label: t("onboarding:step_label_goal") },
      { id: "organisation", label: t("onboarding:step_label_organisation") },
      { id: "get-started", label: t("onboarding:step_label_get_started") },
    ],
    [t],
  );

  if (!isAllowed) {
    return <Navigate to={getRoleHomePath(userRole)} replace />;
  }

  const isGetStarted = currentStep === 2;
  const firstName = authContext.userDetails?.first_name?.trim();

  const headings = [
    {
      title: t("onboarding:goal_title"),
      subtitle: t("onboarding:goal_subtitle"),
    },
    {
      title: t("onboarding:profile_title"),
      subtitle: t("onboarding:profile_subtitle"),
    },
    {
      title: firstName
        ? t("onboarding:welcome_title", { name: firstName })
        : t("onboarding:welcome_title_fallback"),
      subtitle: t("onboarding:welcome_subtitle"),
    },
  ];

  return (
    <div
      id={pageRootTestIds.issuerOnboarding}
      className={`min-h-[calc(100vh-5.1rem)] bg-cfSearchContainer font-CfPrimaryFont mt-[5.1rem] py-6 sm:px-8 sm:py-8 ${
        isGetStarted ? "px-4" : "px-6"
      }`}
    >
      <div className={isGetStarted ? undefined : "mx-auto w-full max-w-[1520px]"}>
        <OnboardingStepper
          steps={steps}
          currentStep={currentStep}
          ariaLabel={t("onboarding:stepper_aria_label")}
        />

        <div className="mx-auto mt-8 w-full">
          <p className="text-xs font-semibold uppercase tracking-wider text-cfTextBreadcrumbDisabled">
            {t("onboarding:step_counter", {
              current: currentStep + 1,
              total: TOTAL_STEPS,
            })}
          </p>

          <h1
            ref={headingRef}
            tabIndex={-1}
            id={automationTestIds.onboardingStepHeading}
            className="mt-2 text-2xl font-semibold text-cfHeadingText sm:text-3xl"
            style={{ outline: "none" }}
          >
            {headings[currentStep].title}
          </h1>
          <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-cfGlobalText">
            {headings[currentStep].subtitle}
          </p>

          <div className="mt-8">
            {currentStep === 0 ? (
              <CategorySelectionStep onComplete={() => goToStep(1)} />
            ) : null}

            {currentStep === 1 ? (
              <OrganisationProfileStep
                onBack={() => goToStep(0)}
                onComplete={() => {
                  goToStep(2);
                  authContext.syncUserPermissions?.();
                }}
              />
            ) : null}

            {currentStep === 2 ? <WelcomeStep /> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssuerOnboarding;
